import { sgenitiv as sgenitivf, shortname } from "./utils";
import { emissionsequence, emissionslist } from "./emissions";
import ce from "./cumulative-emissions";
export const sgenitiv = sgenitivf;

export const RUS = () => 19

function w(A) {
  return Array.isArray(A) || A === undefined ? A : w(A?._sum);
}

/**
 * Prettyprint formatted emissions table, for report automation
 * @param {Object} municipality
 * @returns
 */
export function formattedEmissionsTable(municipality) {
  return Object.keys(municipality.sparse_emissions)
    .sort()
    .map((k) => ({ k, val: municipality.sparse_emissions[k] }))
    .filter(x => x.k !== '_rest')
    .map(
      ({ k, val }) =>
        `${k === "_sum" ? "Summa" : k}:\n${val._sum
          .slice(0, 13) // Years 1990, 2000. 2005, 2010-2019
          .map((x) => round(x / 1000, 1))
          .join("\t")}`
    )
    .join("\n");
}

/**
 * Returns a csv with  actual, forcasted and budgeted emissions from year X to year Y, for report automation.
 * This function is implemented with the same steps as in the user interface (SectionOverview).
 * @param {*} data
 * @returns
 */
export function formattedEmissionSequence(data, { currentYear }) {
  const time_axis = { first_year: 2000, last_year: 2045 }; // Why?

  const emissionsdatasum = emissionslist(data, ["_sum"]);

  const allEmissionsSequences = emissionsequence(
    emissionsdatasum,
    time_axis,
    data.municipality.budget,
    currentYear
  );

  // Pick the last one
  const sequence = allEmissionsSequences[allEmissionsSequences.length - 1];

  // Remove the first and the last values since they are not to be shown in the report figure
  const truncatedSequence = sequence.slice(10, sequence.length - 5);

  // [2010, 2011, ..., 2040]
  const years = Array(truncatedSequence.length)
    .fill()
    .map((_, i) => 2010 + i);

  let csv = `year,emissions,type,region\n`;

  for (let i = 0; i < years.length; i++) {
    let y = years[i];
    let type = y >= currentYear ? "budgeted" : y < 2020 ? "actual" : "forecast";

    csv += `${y},${truncatedSequence[i]},${type},${data.municipality.name}\n`;
  }

  return csv;
}

export function formattedSectorPercentages2018({ emissions, name }) {
  const totalEmissions2018 = emissions._sum._sum[18];

  const sectorList = Object.keys(emissions)
    .filter((sector) => sector !== "_sum")
    .map((sector) => ({
      sector,
      percentage: Math.round(100 * (emissions[sector]._sum[18] / totalEmissions2018))
    }))
    .filter(({ percentage }) => percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);

  let csv = `sector,perc_of_total,region\n`;

  for (const { sector, percentage } of sectorList) {
    csv += `${shortname(sector)},${percentage},${name}\n`;
  }

  return csv;
}

export function formattedSectorPercentages2019({ emissions, name }) {
  const totalEmissions2019 = emissions._sum._sum[19];

  const sectorList = Object.keys(emissions)
    .filter((sector) => sector !== "_sum")
    .map((sector) => ({
      sector,
      percentage: Math.round(100 * (emissions[sector]._sum[19] / totalEmissions2019))
    }))
    .filter(({ percentage }) => percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);

  let csv = `sector,perc_of_total,region\n`;

  for (const { sector, percentage } of sectorList) {
    csv += `${shortname(sector)},${percentage},${name}\n`;
  }

  return csv;
}

export function formattedSectorEmissions({ emissions, name }) {
  let csv = `sector,2010,2011,2012,2013,2014,2015,2016,2017,2018,2019,region\n`;
  for (const sector of Object.keys(emissions)
    .filter((s) => s !== "_sum")
    .filter((s) => s !== "_rest")
    .sort()) {
    let sectorEmissions = emissions[sector]._sum
      .slice(10, 20)
      .map((x) => x / 1000)
      .map((x) => `"${round(x, 1)}"`);

    csv += `${shortname(sector)},${sectorEmissions.join(",")},${name}\n`;
  }

  return csv;
}

export function sum(A) {
  return w(A)?.reduce((a, b) => a + b, 0);
}

export function last(A, n = 1) {
  return w(A)?.slice(-n)[0];
}

export function idx(A, i) {
  return i < 0 
    ? last(A, -i)
    : w(A)?.[i];
}

export function int(x) {
  return round(x, 0);
}

export function roundn(x, n = 0) {
  return Math.round(x * Math.pow(10, n)) * Math.pow(10, -n);
}

// NOTE: Returns a string
export function round(x, n) {
  if (x === null || x === undefined) return x;
  else if (n !== undefined) x = roundn(x, n);
  else if (Math.abs(x) < 1) x = roundn(x, 2);
  else if (Math.abs(x) < 10) x = roundn(x, 1);
  else x = Math.round(x);

  return formatString(x);
}

function formatString(x) {
  return x.toLocaleString("sv-SE");
}

/**
 * If a custom organisation title has been definied, use that one.
 * Otherwise construct it using standard grammar.
 * @param {*} entry
 * @returns {string} organisationTitle
 */
export function organisationTitle(entry) {
  return entry.content.organisationTitle || sgenitiv(entry.name, true) + ' kommun'
}

export function percent(x) {
  return `${int(x * 100)}%`;
}

export function signedpercent(x) {
  if (x > 0) {
    return `+${int(x * 100)}%`;
  } else {
    return `${int(x * 100)}%`;
  }
  //return `${int(x * 100) > 0 ? "+" : ""}${int(x * 100)}%`;
}

export function mean(A) {
  return w(A)?.length ? sum(A) / w(A)?.length : 0.0;
}

export function roughlyrelative(x) {
  if (x < 0.5) return percent(x);
  if (x === 0.5) return "hälften";
  if (x < 3 / 4) return "mer än hälften";
  if (x === 3 / 4) return "¾";
  if (x < 4 / 5) return "mer än ¾";
  if (x === 4 / 5) return "⅘";
  if (x < 0.9) return "mer än ⅘";
  if (x === 0.9) return "90%";
  if (x <= 0.95) return "mer än 90%";
  if (x < 1.0) return "nästan 100%";
  return percent(x);
}

export function cap1(x) {
  return x.slice(0, 1).toUpperCase() + x.slice(1);
}

export function smallestmajority(emissions) {
  const W = ce.sector_weights(emissions, -1);

  // Sort emissions, largest first
  const top = Object.keys(emissions)
    .filter((a) => a !== "_sum")
    .sort((a, b) => W[b] - W[a]);

  let top_n, c;
  for (top_n = 0, c = 0; top_n < top.length && c < 0.5; ++top_n)
    c += W[top[top_n]];

  if (top_n === top.length) c = 1;

  return { names: top.slice(0, top_n), ratio: c };
}

//["a","b","c"] => "a, b och c"
function listnames(names) {
  const last = names.splice(-1);
  if (names.length === 0) return last;
  return names.join(", ") + " och " + last;
}

export function smallestmajority_names(emissions) {
  let { names } = smallestmajority(emissions);
  return listnames(names);
}

export function smallestmajority_ratio(emissions) {
  let { ratio } = smallestmajority(emissions);
  return cap1(roughlyrelative(ratio));
}

export function emissionsPerCapita(municipality, sector = "_sum", i = -1) {
  return (
    idx(municipality.emissions[sector], i) / idx(municipality.population, i)
  );
}

export function emissionsPerCapitaRate(municipality, sector = "_sum", n = 10) {
  return signedpercent(
    Math.pow(
      emissionsPerCapita(municipality, sector, -1) /
        emissionsPerCapita(municipality, sector, -n),
      1 / (n - 1)
    ) - 1
  );
}

//return the average yearly percentage change of emissions per capita between the years with index a and b
//the years a and b work in the same way idx works to select years
export function emissionsPerCapitaRateRange(
  municipality,
  sector = "_sum",
  a = -11,
  b = -1
) {
  // B=A*k^n => k = (B/A)^(1/n)
  const n = b - a;
  const A = emissionsPerCapita(municipality, sector, a);
  const B = emissionsPerCapita(municipality, sector, b);
  const k = Math.pow(B / A, 1 / n);
  //k=0.9 => p = -0.1
  //k=1.2 => p = 0.2
  return signedpercent(k - 1);
}

export function sectorPercent(
  emissions,
  sector = "_sum",
  i = -1,
  sector_ref = "_sum"
) {
  return percent(idx(emissions[sector], i) / idx(emissions[sector_ref], i));
}

function sectorPercentFraction(
  emissions,
  sector = "_sum",
  i = -1,
  sector_ref = "_sum"
) {
  return idx(emissions[sector], i) / idx(emissions[sector_ref], i);
}

export function largestminority(emissions, p) {
  const W = ce.sector_weights(emissions, -1);

  // Sort emissions, largest first
  const top = Object.keys(emissions)
    .filter((a) => a !== "_sum")
    .sort((a, b) => W[b] - W[a]);

  let top_n, c;
  for (top_n = 0, c = 0; top_n < top.length && c < 0.5; ++top_n)
    c += W[top[top_n]];

  if (top_n === top.length) c = 1;

  let names = top.slice(top_n); //all OTHER sectors
  let filterednames = names.filter(
    (name) => sectorPercentFraction(emissions, name) > p / 100
  );

  return filterednames;
}

//return a sentence with some large-ish sectors
//specifically: they are not part of largestminority, but has more than p percent of emissions
//return nothing if there are no large-ish sectors
export function largestminority_text(emissions, p) {
  const filterednames = largestminority(emissions, p);
  if (filterednames.length > 0) {
    return `Utsläppen från ${listnames(
      filterednames
    )} kommer också att behöva minska betydligt innan 2030.`;
  } else {
    return "";
  }
}

export function mermindre(a, b) {
  if (a > b) {
    return "är mer";
  } else if (a < b) {
    return "är mindre";
  } else if (a === b) {
    return "är lika mycket";
  }
  return "";
}

export function overunderrated(a, b) {
  if (a > b) {
    return "underskattade";
  } else if (a < b) {
    return "överskattade";
  } else if (a === b) {
    return "rimligt skattade";
  }
}

export function higherlower(a, b) {
  if (a > b) {
    return "högre än";
  } else if (a < b) {
    return "lägre än";
  } else if (a === b) {
    return "lika stor som";
  }
}
export function absPercent(a, b) {
  return percent(Math.abs(a / b - 1));
}

export function fractionFromDecimal(x) {
  //note: rounding error is bigger the bigger x is
  //so basically try not to use if x>0.5
  //0.1 => 1/10
  //0.12 => 1/8
  return `1/${Math.round(1 / x)}`;
}

export function sumRange(emissions, idx1, idx2) {
  return sum(emissions.slice(idx1, idx2));
}

export function gentitiveCase(str) {
  return `${str}'s`;
}

//subscript
export function sub(str) {
  return `<sub>${str}</sub>`;
}

//superscript
export function sup(str) {
  return `<sup>${str}</sup>`;
}

//recommended annual change to stay within budget
export function requiredpercent(lastemission, remainingtons) {
  return 100 * (Math.exp(-lastemission / remainingtons) - 1);
}

//add a plus before positive numbers (negative numbers already have plus)
export function signed(x) {
  if (x > 0) {
    return `+${x}`;
  } else {
    return `${x}`;
  }
}
